import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { contentHash } from "@/lib/contentHash";

export type ContentTable = "products" | "store_settings";
export interface ContentSource {
  table: ContentTable;
  id: string;
  fields: Record<string, string | null | undefined>;
}

type Saved = { hash: string; text: string };
const keyOf = (t: string, id: string, f: string) => `${t}:${id}:${f}`;
const BATCH = 50;

/**
 * Stage 3 display-only translation of merchant content.
 * Returns tr(table, id, field, original): the translation only when its saved fingerprint matches
 * the current original; otherwise the original (never blank). Default language => no requests.
 */
export function useStoreContentTranslations(
  storeId: string | null | undefined,
  language: string,
  defaultLanguage: string,
  enabledLanguages: string[],
  sources: ContentSource[],
) {
  const active = !!storeId && language !== defaultLanguage && enabledLanguages.includes(language);
  const [saved, setSaved] = useState<Map<string, Saved>>(new Map());
  const [hashes, setHashes] = useState<Map<string, string>>(new Map()); // key|original -> hash
  const requested = useRef(new Set<string>());

  const sig = useMemo(
    () => sources.map((s) => `${s.table}:${s.id}:${Object.values(s.fields).map((v) => v ?? "").join("\u0001")}`).join("\u0002"),
    [sources],
  );

  // Fingerprints of the originals currently on screen.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      const next = new Map<string, string>();
      for (const s of sources) for (const [f, v] of Object.entries(s.fields)) {
        const t = (v ?? "").trim();
        if (t) next.set(`${keyOf(s.table, s.id, f)}|${t}`, await contentHash(t));
      }
      if (!cancelled) setHashes(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sig]);

  const load = useCallback(async () => {
    if (!active || !storeId) return;
    const { data, error } = await (supabase.rpc as any)("get_store_translations_public", { _store_id: storeId, _language: language });
    if (error || !Array.isArray(data)) return;
    const m = new Map<string, Saved>();
    for (const r of data) m.set(keyOf(r.source_table, r.source_id, r.field), { hash: r.source_hash, text: r.translated_text });
    setSaved(m);
  }, [active, storeId, language]);

  useEffect(() => { setSaved(new Map()); if (active) load(); }, [active, load]);

  // Request translations for missing or stale fields (references only — never text).
  useEffect(() => {
    if (!active || !storeId || hashes.size === 0) return;
    const need: { table: ContentTable; id: string }[] = [];
    for (const s of sources) {
      let stale = false;
      for (const [f, v] of Object.entries(s.fields)) {
        const t = (v ?? "").trim();
        if (!t || t.length > 3000) continue;
        const h = hashes.get(`${keyOf(s.table, s.id, f)}|${t}`);
        const cur = saved.get(keyOf(s.table, s.id, f));
        if (h && (!cur || cur.hash !== h) && !requested.current.has(`${language}|${keyOf(s.table, s.id, f)}|${h}`)) {
          requested.current.add(`${language}|${keyOf(s.table, s.id, f)}|${h}`);
          stale = true;
        }
      }
      if (stale) need.push({ table: s.table, id: s.id });
    }
    if (!need.length) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < need.length; i += BATCH) {
        try {
          await supabase.functions.invoke("translate-store-content", {
            body: { storeId, language, items: need.slice(i, i + BATCH) },
          });
        } catch { /* originals stay visible */ }
      }
      if (!cancelled) load();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, storeId, language, hashes, saved]);

  const tr = useCallback((table: ContentTable, id: string, field: string, original: string | null | undefined): string => {
    const orig = original ?? "";
    if (!active) return orig;
    const t = orig.trim();
    if (!t) return orig;
    const cur = saved.get(keyOf(table, id, field));
    const h = hashes.get(`${keyOf(table, id, field)}|${t}`);
    return cur && h && cur.hash === h && cur.text.trim() ? cur.text : orig;
  }, [active, saved, hashes]);

  return { tr };
}
